import { useState } from 'react'
import { pushToEbis } from '../lib/ebisPush'

/**
 * Shared push-to-EBIS confirm modal used by all three push surfaces.
 *
 * Props:
 *   items          — [{ task_number, task_name, labor_hours?, parts_cost?, _note? }]
 *                    _note: optional badge text (e.g. 'Manually set') shown on the item card
 *   regNum         — aircraft registration (for new WO/quote)
 *   workOrder      — existing WO/quote number (for add-to-existing)
 *   type           — 'quote' (default) | 'work-order'
 *   label          — short display string shown in the modal header subtitle
 *   onClose        — () => void — called when the user dismisses the modal
 *   onSuccess      — (woNumber) => void — called right after a successful push (before result UI)
 *   successActions — (woNumber) => ReactNode — optional extra buttons rendered in the success state
 */
export default function EbisPushConfirmModal({
  items,
  regNum,
  workOrder,
  type = 'quote',
  label,
  onClose,
  onSuccess,
  successActions,
}) {
  const [pushing, setPushing] = useState(false)
  const [result, setResult]   = useState(null)   // { woNumber, woLink }
  const [error, setError]     = useState(null)   // Error object

  const handleConfirm = async () => {
    setPushing(true)
    setError(null)
    try {
      const { woNumber, woLink } = await pushToEbis({ items, regNum, workOrder, type })
      onSuccess?.(woNumber)
      setResult({ woNumber, woLink })
    } catch (e) {
      setError(e)
    } finally {
      setPushing(false)
    }
  }

  const typeLabel = type === 'work-order' ? 'Work Order' : 'Quote'
  const blankCount = items.filter(i => i.labor_hours == null && i.parts_cost == null).length

  return (
    <div className="fixed inset-0 z-[60] bg-gray-900/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[80vh]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          {result ? (
            <div>
              <h3 className="text-base font-semibold text-green-700">{typeLabel} Created</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {label && <span>{label} · </span>}
                <span className="font-mono">#{result.woNumber}</span>
              </p>
            </div>
          ) : (
            <div>
              <h3 className="text-base font-semibold text-gray-900">Push to EBIS</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {label ? `${label} · ` : ''}
                {items.length} item{items.length !== 1 ? 's' : ''}
                {blankCount > 0 && (
                  <span className="text-amber-600 ml-1">· {blankCount} with no cost data</span>
                )}
              </p>
            </div>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Item list */}
        {!result && (
          <div className="flex-1 overflow-auto px-6 py-4 space-y-2">
            {items.map((item, i) => {
              const hasData = item.labor_hours != null || item.parts_cost != null
              return (
                <div
                  key={i}
                  className={`rounded-xl border px-4 py-3 ${item._note ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-white'}`}
                >
                  {item.task_number && (
                    <div className="text-xs font-mono text-gray-400 mb-0.5">{item.task_number}</div>
                  )}
                  <div className="text-sm font-medium text-gray-800 leading-snug">{item.task_name || '—'}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {item._note && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        {item._note}
                      </span>
                    )}
                    {item.labor_hours != null && (
                      <span className="text-gray-600">
                        Hours: <span className="font-medium text-gray-800">{Number(item.labor_hours).toFixed(1)}</span>
                      </span>
                    )}
                    {item.parts_cost != null && (
                      <span className="text-gray-600">
                        Parts: <span className="font-medium text-gray-800">${Math.round(item.parts_cost).toLocaleString('en-US')}</span>
                      </span>
                    )}
                    {!hasData && (
                      <span className="text-amber-600">No cost data — will push blank</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Success state */}
        {result && (
          <div className="px-6 py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-900 font-mono">#{result.woNumber}</p>
            <p className="text-xs text-gray-400 mt-1">
              {items.length} item{items.length !== 1 ? 's' : ''} pushed to EBIS
            </p>
            {result.woLink && (
              <a
                href={result.woLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs text-brand-red hover:underline"
              >
                Open in EBIS →
              </a>
            )}
            {successActions && (
              <div className="mt-4 flex flex-col items-center gap-2">
                {successActions(result.woNumber)}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-6 mb-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {error.code === 'AIRCRAFT_NOT_FOUND'
              ? `Aircraft not found in EBIS — add the registration there first, then retry.`
              : error.message}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleConfirm}
              disabled={pushing}
              className="px-4 py-2 text-sm font-medium bg-brand-red text-white rounded-xl hover:bg-brand-red-dark disabled:opacity-40 transition-colors"
            >
              {pushing ? 'Pushing…' : 'Confirm Push'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
